import {
  useCallback,
  useEffect,
  useRef,
  type MouseEvent,
  type MutableRefObject,
  type RefObject,
} from "react";
import { getEditorStoreState } from "../domain/store";
import {
  isTextBlockEditing,
  isTextFrameChromeTarget,
  isInlineMergeTagAutocompleteTarget,
  isTextToolbarTarget,
} from "../domain/text-editing";
import type { ButtonRowBlock, Selection, TextBlockTarget } from "../domain/types";
import { emailBuilderLabelCommitUpdate } from "../editing/richText/emailBuilderRichTextHtml";
import {
  placeEmailBuilderRichTextCaret,
  readEmailBuilderRichTextStoredHtml,
  setEmailBuilderRichTextCaretPoint,
} from "../editing/richText/emailBuilderRichTextSession";

function readInlineButtonEditorElement(editorId: string): HTMLElement | null {
  return document.querySelector(
    `[data-email-builder-inline-button-label="${editorId}"]`,
  );
}

type UseEditableButtonRowSlotEditSessionParams = {
  rowId: string;
  columnId: string;
  block: ButtonRowBlock;
  slotIndex: number;
  editorId: string;
  isEditing: boolean;
  setSelection: (selection: Selection) => void;
  setTextEditingBlock: (target: TextBlockTarget | null) => void;
  updateBlock: (
    rowId: string,
    columnId: string,
    blockId: string,
    patch: Partial<ButtonRowBlock>,
  ) => void;
  setTextEditReentryGuard: (blockId: string | null) => void;
  rootRef: RefObject<HTMLDivElement | null>;
  exitingRef: MutableRefObject<boolean>;
  toolbarPressRef: MutableRefObject<boolean>;
};

export function useEditableButtonRowSlotEditSession({
  rowId,
  columnId,
  block,
  slotIndex,
  editorId,
  isEditing,
  setSelection,
  setTextEditingBlock,
  updateBlock,
  setTextEditReentryGuard,
  rootRef,
  exitingRef,
  toolbarPressRef,
}: UseEditableButtonRowSlotEditSessionParams) {
  const slot = block.buttons[slotIndex];

  const exitEdit = useCallback(
    (setGuard: boolean) => {
      const editor = readInlineButtonEditorElement(editorId);
      exitingRef.current = true;
      const nextLabel = readEmailBuilderRichTextStoredHtml(editorId);
      const labelUpdate = nextLabel === null || slot === undefined
        ? null
        : emailBuilderLabelCommitUpdate(slot.label, slot.fontWeight, nextLabel);
      if (labelUpdate !== null && slot) {
        const buttons = [...block.buttons] as ButtonRowBlock["buttons"];
        const current = buttons[slotIndex];
        if (current) {
          buttons[slotIndex] = {
            ...current,
            label: labelUpdate.text,
            fontWeight: labelUpdate.fontWeight,
          };
          updateBlock(rowId, columnId, block.id, { buttons });
        }
      }
      setTextEditingBlock(null);
      if (setGuard) {
        setTextEditReentryGuard(editorId);
      }
      editor?.blur();
      queueMicrotask(() => {
        exitingRef.current = false;
      });
    },
    [
      block,
      columnId,
      editorId,
      exitingRef,
      rowId,
      setTextEditReentryGuard,
      setTextEditingBlock,
      slot,
      slotIndex,
      updateBlock,
    ],
  );

  const exitEditRef = useRef(exitEdit);

  useEffect(() => {
    exitEditRef.current = exitEdit;
  }, [exitEdit]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    let cancelled = false;
    let blurTimeoutId = 0;

    const onPointerDown = (event: PointerEvent) => {
      if (exitingRef.current) {
        return;
      }
      const root = rootRef.current;
      const hit = event.target;
      if (!(hit instanceof Node)) {
        return;
      }
      const editor = readInlineButtonEditorElement(editorId);
      if (root?.contains(hit) || editor?.contains(hit)) {
        return;
      }
      if (isTextToolbarTarget(hit) || isInlineMergeTagAutocompleteTarget(hit)) {
        return;
      }
      const frame = rootRef.current?.closest('[data-level="block"]');
      if (isTextFrameChromeTarget(hit, frame)) {
        return;
      }
      exitEditRef.current(true);
    };

    const onEditorBlur = (event: FocusEvent) => {
      if (
        exitingRef.current ||
        isTextToolbarTarget(event.relatedTarget) ||
        isInlineMergeTagAutocompleteTarget(event.relatedTarget)
      ) {
        return;
      }
      if (toolbarPressRef.current) {
        return;
      }
      blurTimeoutId = window.setTimeout(() => {
        if (cancelled || exitingRef.current || toolbarPressRef.current) {
          return;
        }
        const editing = getEditorStoreState().textEditingBlock;
        if (isTextBlockEditing(editing, rowId, columnId, block.id, slotIndex)) {
          exitEditRef.current(true);
        }
      }, 0);
    };

    const onFocusOut = (event: FocusEvent) => {
      const editor = readInlineButtonEditorElement(editorId);
      if (editor === null) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node) || !editor.contains(target)) {
        return;
      }
      const relatedTarget = event.relatedTarget;
      if (relatedTarget instanceof Node && editor.contains(relatedTarget)) {
        return;
      }
      onEditorBlur(event);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusout", onFocusOut, true);

    return () => {
      cancelled = true;
      window.clearTimeout(blurTimeoutId);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusout", onFocusOut, true);
    };
  }, [
    block.id,
    columnId,
    editorId,
    exitingRef,
    isEditing,
    rootRef,
    rowId,
    slotIndex,
    toolbarPressRef,
  ]);

  function enterEdit(at?: { x: number; y: number }) {
    if (at) {
      setEmailBuilderRichTextCaretPoint(editorId, at);
    }
    setSelection({
      kind: "buttonRowSlot",
      rowId,
      columnId,
      blockId: block.id,
      slotIndex,
    });
    setTextEditingBlock({
      rowId,
      columnId,
      blockId: block.id,
      slotIndex,
    });
  }

  return { enterEdit };
}

type UseEditableButtonRowSlotPointerParams = {
  editorId: string;
  isEditing: boolean;
  selected: boolean;
  setSelection: (selection: Selection) => void;
  setTextEditReentryGuard: (blockId: string | null) => void;
  slotSelection: Selection & { kind: "buttonRowSlot" };
  enterEdit: (at?: { x: number; y: number }) => void;
};

export function useEditableButtonRowSlotPointer({
  editorId,
  isEditing,
  selected,
  setSelection,
  setTextEditReentryGuard,
  slotSelection,
  enterEdit,
}: UseEditableButtonRowSlotPointerParams) {
  const editPointerStartRef = useRef<{ x: number; y: number } | null>(null);

  function handleMouseDown(event: MouseEvent) {
    const editor = readInlineButtonEditorElement(editorId);
    if (isEditing && editor) {
      if (isTextToolbarTarget(event.target)) {
        editPointerStartRef.current = null;
        return;
      }
      if (event.target instanceof Node && editor.contains(event.target)) {
        editPointerStartRef.current = null;
      } else if (event.button === 0) {
        editPointerStartRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
      }
      return;
    }
    if (event.button !== 0) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    event.stopPropagation();
    if (!selected) {
      setTextEditReentryGuard(null);
      setSelection(slotSelection);
      return;
    }
    editPointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
    };
  }

  function handleMouseUp(event: MouseEvent) {
    if (event.button !== 0) {
      return;
    }
    const editor = readInlineButtonEditorElement(editorId);
    if (isEditing && editor) {
      if (isTextToolbarTarget(event.target)) {
        editPointerStartRef.current = null;
        return;
      }
      const hit = event.target;
      const start = editPointerStartRef.current;
      editPointerStartRef.current = null;
      if (start && hit instanceof Node && !editor.contains(hit)) {
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (dx * dx + dy * dy < 64) {
          placeEmailBuilderRichTextCaret(editorId, event.clientX, event.clientY);
        }
      }
      return;
    }
    const start = editPointerStartRef.current;
    editPointerStartRef.current = null;
    if (!start || !selected) {
      return;
    }
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (dx * dx + dy * dy < 64) {
      setTextEditReentryGuard(null);
      enterEdit({ x: event.clientX, y: event.clientY });
    }
  }

  function handleDoubleClick(event: MouseEvent) {
    event.stopPropagation();
    if (isEditing) {
      return;
    }
    setTextEditReentryGuard(null);
    enterEdit({ x: event.clientX, y: event.clientY });
  }

  function handleClick(event: MouseEvent) {
    event.stopPropagation();
  }

  return {
    rootHandlers: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onDoubleClick: handleDoubleClick,
      onClick: handleClick,
    },
  };
}
